import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { Trans, useTranslation } from 'next-i18next'
// Dependencies.
import styled from 'styled-components'

import { Page } from '../components/Page'
import { GetStarted } from '../components/GetStarted'
import { Section } from '../components/Section'
import { H1 } from '../components/H1'
import { H2 } from '../components/H2'
import { H4 } from '../components/H4'
import { P } from '../components/P'

// Privacy Page.
const PrivacyPage = () => {
  const { t } = useTranslation('common')
  return (
    <Page>
      <Section>
        <Div>
          <H1>{t(`Privacy Policy`, `Privacy Policy`)}</H1>
          <P>
            <Trans i18nKey="privacy2028595374">
              This Privacy Policy explains how Klyk (referred to in this policy
              as “<strong>we</strong>”, “<strong>us</strong>” or “
              <strong>our</strong>”) and/or its subsidiaries or affiliates use,
              collects, stores and discloses information supplied to us whilst
              using our services through Klyk website located at{' '}
              <a href="https://www.ourklyk.com/"> www.ourklyk.com</a> and other
              websites, software applications, services, devices (including, but
              not limited to, any mobile devices) or networks owned or
              controlled by Klyk that allow for the distribution and reception
              of video, audio, and other content (we will refer to these
              collectively as the “<strong>Klyk Services</strong>”). Any
              capitalised terms not otherwise defined herein will have the
              meaning ascribed to them in our Terms of Service located{' '}
              <a href="https://ourklyk.com/terms">here</a>.
            </Trans>
            <br />
            <br />
            <Trans i18nKey="privacy20211224161208707655625">
              This Privacy Policy details how we comply with the Privacy Act
              1988 (Cth), the Australian Privacy Principles, and the EU General
              Data Protection Regulation 2016/679 (<strong>GDPR</strong>), which
              govern the collection, storage, use, and disclosure of your
              information and data.
            </Trans>
            <br />
            <br />
            {t(
              'privacy20211224145624652378101',
              'We may update the privacy policy from time to time at our sole discretion. Any variations become effective on posting the updated privacy policy and we shall have no obligation to provide you with individual notice of such changes. Your continued use of the Klyk Services following the publication of any amended privacy policy shall signify your acceptance of that amended privacy policy, except where we are otherwise obliged by law to seek your direct consent.',
            )}
            <br />
            <br />
            <strong>
              {t(
                'privacy20211224145704744919302',
                'Please read this privacy policy carefully before using Klyk Services. If you do not agree with any part of this privacy policy, please do not use the Klyk Services.',
              )}
            </strong>
          </P>

          {/* What Information */}
          <H2>{t(`privacy980315618`, `WHAT INFORMATION DO WE COLLECT?`)}</H2>
          <P>
            {t(
              'privacy20211224145805528830635',
              'This privacy policy covers all information collected via your use of the Klyk Services. The information we collect can be broken down into the following types:',
            )}
            <br />
            <br />
            <Trans i18nKey="privacy230930383">
              ‘<strong>Personal Information</strong>’ means information that can
              be used to personally identify you such as your name, email
              address, contact number, profile pictures, and payment details. We
              do not collect or process the Personal Information of anyone under
              the age of 18 without the express consent of their parent or
              guardian.
            </Trans>
            <br />
            <br />
            <Trans i18nKey="privacy20211224150004445427446">
              ‘<strong>Usage Information</strong>’ means anonymous aggregate
              data that is automatically collected through your use of the Klyk
              Services. This includes information that identifies your device,
              your operating system, your IP address and dates and times that
              you access and use the Klyk Services. This information is used for
              statistical analysis to help us to improve the Klyk Services to
              the benefit of all users.
            </Trans>
            <br />
            <br />
            <Trans i18nKey="privacy20211224150030657659256">
              The GDPR recognises that Usage Information, whilst for the most
              part anonymous, can be cumulatively used to directly or indirectly
              identify you. Usage Information that can be used to identify you
              in any way, together with your Personal Information, shall
              collectively be referred to in this privacy policy as ‘
              <strong>Personal Data</strong>’.
            </Trans>
          </P>

          {/* How Information */}
          <H2>{t(`privacy-1875081705`, `HOW WE COLLECT YOUR INFORMATION`)}</H2>
          <P>
            {t(
              `privacy-779540463`,
              `Personal Information is collected directly from you when you:`,
            )}
          </P>
          <ul>
            <li>
              <P>
                <strong>
                  {t('Register an account', 'Register an account')}
                </strong>
                <br />
                {t(
                  'privacy20211224150254542041851',
                  'We ask you to provide personal details including but not limited to your name, address, etc. for the purpose of creating your account and corresponding with you about your use of the Klyk Services. If you do not provide these details, you will not be able to register as a user and you may be limited in how you can access the Klyk Services.',
                )}{' '}
                <br />
                <br />
                {t(
                  'privacy20211224150327326224680',
                  'Additional information you can provide on your account is a profile picture… Whilst providing these details will enhance your user experience, it is not required to access the Klyk Services and you provide such information at your own election.',
                )}{' '}
              </P>
            </li>
            <li>
              <P>
                <strong>
                  {t(`privacy-740218654`, `Contact Our Support Team`)}
                </strong>
                <br />
                {t(
                  `privacy-1930271914`,
                  `When you contact our support team via our website, phone or email, we may collect your name, contact details, and other Personal Information for the purpose of providing assistance to you.`,
                )}
              </P>
            </li>
            <li>
              <P>
                <strong>{t(`Make a payment`, `Make a payment`)}</strong>
                <br />
                {t(
                  `privacy1508820156`,
                  `If you submit a payment through the Klyk Services, we may collect credit card details, or otherwise see the details of any bank transaction processed by you. If you do not authorise us to have access to your payment details, you may not be able to access or use the Klyk Services.`,
                )}
              </P>
            </li>
            <li>
              <P>
                <strong>
                  {t(`privacy1807044890`, `Opt into our mailing list`)}
                </strong>
                <br />
                {t(
                  `privacy-1094371647`,
                  `From time to time you may be able to subscribe to mailing lists or other direct marketing communications.`,
                )}
              </P>
            </li>
            <li>
              <P>
                <strong>
                  {t(
                    `privacy2005534940`,
                    `Access, browse, or use the Klyk Services`,
                  )}
                </strong>
                <br />
                {t(
                  `privacy868065947`,
                  `We may otherwise collect information from you when you access, browse, use, or otherwise interact with the Klyk Services.`,
                )}
              </P>
            </li>
          </ul>
          <P>
            {t(
              `privacy-1154270402`,
              `It is your choice to provide Personal Information to us. Wherever it is lawful and practicable, you have the option not to identify yourself when interacting with us. Please be aware that it may be necessary for us to collect your Personal Information to enable us to provide the Klyk Services to you. As such, if you do not wish to provide your Personal Information, we may not be able to provide the Klyk Services to you in a fully operational form.`,
            )}
          </P>

          {/* Cookies */}
          <H2>{t(`COOKIES`, `COOKIES`)}</H2>
          <P>
            <Trans i18nKey="privacy880220833">
              We may collect anonymous Usage Data from the Klyk Services which
              utilises cookies, pixel tags and other tracking technologies
              (collectively <strong>Cookies</strong>). Cookies are small packets
              of data that are downloaded onto your device when you access a
              website. Cookies hold specific information that helps a website
              ‘remember’ your actions and preferences over time. These are the
              types of Cookies that we may use to operate the Klyk Services:
            </Trans>
          </P>

          <ul>
            <li>
              <P>
                {t(
                  `privacy1019894964`,
                  `Strictly Necessary Cookies – these Cookies are essential to ensure that the Klyk Services work correctly, and record information that allows you to move around the Klyk Services and navigate its features;`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `privacy1896332166`,
                  `Performance Cookies – these Cookies collect information about how you use the Klyk Services, such as how often you access the Klyk Services and if you encounter any errors.`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `privacy-1445447748`,
                  `Functionality Cookies – these Cookies allow our App or Website to remember the choices you make to provide a more personalised experience.`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `privacy-2054884260`,
                  `Targeting/Advertising Cookies  – these Cookies deliver targeted advertising to you based on your interests and use of the Klyk Services.`,
                )}
              </P>
            </li>
          </ul>
          <P>
            <Trans i18nKey="privacy1585957108">
              Cookies can stay on your device temporarily (
              <strong>Session Cookies</strong>) or until you manually delete
              them (<strong>Persistent Cookies</strong>). You can adjust your
              browser settings at any time to block Cookies, however please note
              that doing so may limit our ability to provide the Klyk Services
              to you.
            </Trans>
            <br />
            <br />
            {t(
              `privacy97144187`,
              `To request a full list of the individual Cookies we use, please email our privacy officer at admin@ourklyk.com.`,
            )}
          </P>
          <H2>{t(`privacy-1352403653`, `THIRD PARTY SITES`)}</H2>
          <P>
            {t(
              `privacy278895640`,
              `The Klyk Services may contains plugins and links to third party sites to enhance your user experience. Please note that this Privacy Policy does not cover the privacy practices of third party sites which may be integrated or otherwise linked to Klyk Services. Please refer directly to the privacy policies and statements of any third party site to obtain information regarding their data collection, use, and disclosure policies.`,
            )}
          </P>
          <P>
            {t(
              `privacy-1159675117`,
              `We do not have access to, or control over, the technologies that these third parties or Plug-in Operators may use to collect information about you. We disclaim all liability in connection with the services of these third party sites or Plug-in Operators. If you have any concerns about third party  sites or Plug-ins integrated or otherwise linked to the Klyk Services, we encourage you to reach out to them directly.`,
            )}
          </P>

          {/* Third Party */}
          <H2>{t(`privacy377275514`, `HOW WE USE YOUR INFORMATION`)}</H2>
          <P>
            {t(
              `privacy-677388304`,
              `Legimate purposes that you agree we may use your Personal Data for include but are not limited to the following:`,
            )}
          </P>
          <ul>
            <li>
              <P>{t(`privacy1701546943`, `to confirm your identity;`)}</P>
            </li>
            <li>
              <P>
                {t(`privacy1357719463`, `to provide the Klyk Services to you;`)}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `privacy-179032345`,
                  `to respond to requests submitted by you;`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `privacy-489097231`,
                  `to process any payment made by you in connection with the Klyk Services;`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `privacy2096342409`,
                  `to improve the functionality of the Klyk Services;`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `privacy-2090563002`,
                  `to prevent, detect and investigate potential illegal activities, security breaches and fraud;`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `privacy-676059263`,
                  `to contact you via email to provide you with periodical promotional emails about new products or other information which we think you may find interesting, only where you have opted in to receiving such communication and until you ‘opt out’.`,
                )}
              </P>
            </li>
          </ul>
          <P>
            {t(
              `privacy660907442`,
              `For the avoidance of doubt, we will only use your Personal Data for purposes that you would reasonably expect us to use your Personal Data for in connection with providing the Klyk Services to you, or where we are required by law to collect your Personal Data. We will not sell, rent, or license your email address or any of your Personal Data.`,
            )}
          </P>
          <P>
            {t(
              `privacy-796377823`,
              `We will retain your Personal Data for the period necessary to fulfill the purposes outlined in this Privacy Policy unless a longer retention period is required or permitted by law. How long we retain your Personal Data depends on the type of data and the legitimate purpose for which we process the data.`,
            )}
          </P>
          <H4>{t(`'Opt Out'`, `'Opt Out'`)}</H4>
          <P>
            {t(
              `privacy-1270327023`,
              `We recognise your right under the Spam Act 2003 (Cth) and the GDPR to opt out from direct marketing, and as such these consents can be modified at any time by emailing us at admin@ourklyk.com or clicking unsubscribe on any direct marketing communications.`,
            )}
          </P>

          {/* Who we Disclose */}
          <H2>
            {t(`privacy-988473538`, `Who we disclose your information to`)}
          </H2>
          <P>
            {t(
              `privacy-874419813`,
              `We do not disclose your Personal Data except in the limited circumstances described here. You agree and consent to us disclosing your Personal Data to:`,
            )}
          </P>
          <ul>
            <li>
              <P>
                <strong>{t(`Other Klyk Users`, `Other Klyk Users`)}</strong>
                <br />
                {t(
                  `privacy-1116655608`,
                  `When you use the Klyk Services, some data will be disclosed to other participants and to meeting or webinar hosts. For instance, when you attend a meeting, your name might appear in the attendee list. If you turn on your video camera, your image will be shown. If you send a chat or share content, that can be viewed by others in the chat or the meeting.`,
                )}
              </P>
            </li>
            <li>
              <P>
                <strong>
                  {t(
                    `privacy-1939888613`,
                    `Employees, contractors, and officers of Klyk`,
                  )}
                </strong>
              </P>
            </li>
            <li>
              <P>
                <strong>
                  {t(
                    `privacy1713579665`,
                    `Authorities, including police, regulators or as otherwise required by law`,
                  )}
                </strong>
              </P>
            </li>
            <li>
              <P>
                <strong>
                  {t(`privacy267274519`, `Third party professional agencies `)}
                </strong>
                <br />
                {t(
                  `privacy-2043112330`,
                  `This includes third parties who have been engaged by us to assist in the provision of the Klyk Services, including but not limited to legal, financial, and marketing advisors.`,
                )}
              </P>
            </li>
          </ul>
          <P>
            {t(
              `privacy1258416078`,
              `For the avoidance of doubt, all employees, consultants, contractors and agents of ours are bound by Australian privacy laws.`,
            )}
            <br />
            <br />
            {t(
              `privacy1702760582`,
              `You can withdraw your consent for us to share your Personal Data with third parties at any time by emailing us at admin@ourklyk.com, but please note that withdrawal of such consents may affect your ability to access and use the Klyk Services.`,
            )}
          </P>

          {/* Your rights */}
          <H2>{t(`privacy440529708`, `Your rights to your information`)}</H2>
          <P>
            {t(
              `privacy1253274132`,
              `You have a general right to access any Personal Information that is held about you by us, unless a valid exception applies. You can request this at any time by contacting admin@ourklyk.com. Please note that we cannot modify your Personal Information. You acknowledge that it is your responsibility to maintain the truth, accuracy, and completeness of your information and your failure to do so may inhibit our ability to provide the Klyk Services. You acknowledge and agree that you remain solely responsible for maintaining the truth, accuracy, and completeness of your information at all times, and we shall have no liability to you or any third party arising from your failure to do the same. You can modify your own information at any time by emailing us at admin@ourklyk.com.`,
            )}
            <br />
            <br />
            {t(
              `privacy-1325252551`,
              `In accordance with the GDPR, we additionally acknowledge the specific rights of EU subjects to:`,
            )}
          </P>

          <ul>
            <li>
              <P>
                {t(
                  `privacy1443318277`,
                  `have their data erased that is no longer being used for a legitimate purpose;`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `privacy-1405912823`,
                  `request a copy of all Personal Data held about them in a readable format, along with supplementary information to verify that such Personal Data is being processed lawfully; and`,
                )}
              </P>
            </li>
            <li>
              <P>
                {t(
                  `privacy-1202036468`,
                  `request restricted processing of their Personal Data whilst any complaints or concerns are being resolved.`,
                )}
              </P>
            </li>
          </ul>
          <P>
            {t(
              `privacy-2013007309`,
              `To erase, request or restrict processing of your Personal Data, please email us at admin@ourklyk.com, or follow the prompts in your User Profile Settings.`,
            )}
          </P>

          {/* Security */}
          <H2>{t(`Security`, `Security`)}</H2>
          <P>
            {t(
              `privacy-1619295371`,
              `We protect your Personal Data through technical security measures i.e. firewalls, encryption that limit the risk of loss, disclosure, or unauthorised access. No security measures are, however, 100% secure so we cannot guarantee the security of your information or data at any time. To the extent permitted by law, we accept no liability for any breach of security, or direct hacking of our security measures, or any unintentional disclosure, loss or misuse of any information or data or for the actions of any third parties that may obtain any information or data.`,
            )}
            <br />
            <br />
            {t(
              `privacy-1431271762`,
              `Notwithstanding the above, we acknowledge our obligation to report any data breach that is likely to risk the rights and freedoms of natural persons to the Australian Information Commissioner and, where our data breach involves the information of EU subjects, report to the European Data Protection Supervisor. We will also inform you, where possible, if your data has been breached in the circumstance where it poses a risk of serious harm or your rights and freedoms.`,
            )}
            <br />
            <br />
            {t(
              `privacy-563130798`,
              `We also train all staff who may have access to your Personal Data about this privacy policy and our obligations under the Privacy Act, Privacy Principles, and GDPR. For more information on our internal policies, email us at admin@ourklyk.com.`,
            )}
          </P>

          {/* Overseas */}
          <H2>{t(`OVERSEAS DISCLOSURE`, `OVERSEAS DISCLOSURE`)}</H2>
          <P>
            {t(
              `privacy1706519255`,
              `We may, in the course of providing the Klyk Services to you, transfer your Personal Data to overseas countries that are deemed by the EU Commission as having an ‘adequate’ level of Personal Data protection. Where we transfer data to a third party in a country where no adequacy decision has been made, we warrant that any person or entity handling your data in those countries are bound under contract to meet the requirements of the Privacy Act, Privacy Principles and GDPR.`,
            )}
          </P>

          {/* Contact */}
          <H2>{t(`Contact`, `Contact`)}</H2>
          <P>
            {t(
              `privacy1421059651`,
              `Thank you for taking the time to read our privacy policy. If you have any questions regarding our privacy policy, you can reach our privacy officer at admin@ourklyk.com.`,
            )}
            <br />
            <br />
            <Trans i18nKey="privacy510971636">
              If you are not satisfied with our handling of your Personal Data,
              or have any other concern over our privacy policy, then you may
              lodge a formal complaint with the Office of the Australian
              Information Commissioner (for more information, please see{' '}
              <a href="https://www.oaic.gov.au">www.oaic.gov.au</a>) or with the
              European Data Protection Supervisor (for more information, please
              see <a href="https://edps.europa.eu">https://edps.europa.eu</a>).
            </Trans>
          </P>
        </Div>
      </Section>
      <GetStarted />
    </Page>
  )
}

// Styled Components
const Div = styled.div`
  margin: 0 auto;
  width: 100%;
  max-width: 664px;
  padding: 150px 0;

  h1 {
    margin-bottom: 60px;
  }
  h2 {
    text-transform: uppercase;
    margin-top: 60px;
  }
  h4 {
    margin-top: 30px;
  }
  p {
    margin-top: 15px;
  }
`

// Export.
export default PrivacyPage
export async function getStaticProps({ locale }) {
  const translations = await serverSideTranslations(locale, ['common'])
  return {
    props: {
      ...translations,
    },
  }
}
